import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";


const snapshotItemForAdminLeg = (item) => ({
  ...item,
  contracts: Number(item?.contracts ?? item?.size ?? 0) || 0,
});

const defaultFetchImpl = (url, init = {}) =>
  fetch(url, { credentials: "include", ...init });

const formatSnapshotTime = (timestamp) => {
  const numericValue = Number(timestamp);
  if (!Number.isFinite(numericValue) || numericValue <= 0) return "-";
  return new Date(numericValue).toLocaleString("ko-KR", {
    month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
    hour12: false,
  });
};

const formatSnapshotNumber = (value, digits = 2) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return "-";
  return numericValue.toLocaleString("en-US", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
};

const actionClass = (action) => {
  if (!action) return "";
  const a = String(action).toLowerCase().replace(/_/g, " ").replace(/\s+/g, " ").trim();
  if (a === "open long") return "order-history-modal__action--open-long";
  if (a === "open short") return "order-history-modal__action--open-short";
  if (a === "close long") return "order-history-modal__action--close-long";
  if (a === "close short") return "order-history-modal__action--close-short";
  return "";
};

const isCloseHistoryAction = (action) => String(action || "").toLowerCase().includes("close");

const formatNullableNumber = (value, digits = 4) => {
  if (value == null || value === "") return "—";
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return "—";
  return numericValue.toLocaleString("en-US", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
};

const pnlToneClass = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "";
  if (n > 0) return "order-history-modal__pnl--profit";
  if (n < 0) return "order-history-modal__pnl--loss";
  return "order-history-modal__pnl--zero";
};

const pendingKindLabel = (kind) => {
  if (kind === "trigger") return "트리거";
  if (kind === "limit") return "지정가";
  return kind || "-";
};

const pendingKindClass = (kind) => {
  if (kind === "trigger") return "order-history-modal__kind--trigger";
  if (kind === "limit") return "order-history-modal__kind--limit";
  return "";
};

const appendUniqueHistoryRows = (prevRows, nextRows) => {
  const seen = new Set(
    (Array.isArray(prevRows) ? prevRows : []).map((row) => String(row?.id ?? ""))
  );
  const merged = [...(Array.isArray(prevRows) ? prevRows : [])];
  for (const row of Array.isArray(nextRows) ? nextRows : []) {
    const key = String(row?.id ?? "");
    if (key && seen.has(key)) continue;
    if (key) seen.add(key);
    merged.push(row);
  }
  return merged;
};

const buildHistoryUrl = (baseEndpoint, { account, beforeId } = {}) => {
  const params = new URLSearchParams();
  if (account) params.set("account", account);
  if (beforeId) params.set("before_id", beforeId);
  const qs = params.toString();
  return qs ? `${baseEndpoint}?${qs}` : baseEndpoint;
};

// ─── 주문내역 팝업 ──────────────────────────────────────────────────────────
function OrderHistoryModal({ historyEndpoint, pendingOrdersEndpoint, cancelPendingOrderEndpoint, fetchImpl, onClose }) {
  const [viewMode, setViewMode]           = useState("filled");
  const [loading, setLoading]             = useState(true);
  const [loadingMore, setLoadingMore]     = useState(false);
  const [error, setError]                 = useState("");
  const [loadMoreError, setLoadMoreError] = useState("");
  const [mainHistory, setMainHistory]     = useState([]);
  const [secondHistory, setSecondHistory] = useState([]);
  const [mainPending, setMainPending]     = useState([]);
  const [secondPending, setSecondPending] = useState([]);
  const [hasSecondAccount, setHasSecondAccount] = useState(false);
  const [mainHasMore, setMainHasMore]     = useState(false);
  const [secondHasMore, setSecondHasMore] = useState(false);
  const [mainNextBeforeId, setMainNextBeforeId] = useState(null);
  const [secondNextBeforeId, setSecondNextBeforeId] = useState(null);
  const [activeTab, setActiveTab]         = useState("main");
  const [cancellingId, setCancellingId]   = useState(null);
  const [cancelNotice, setCancelNotice]   = useState("");
  /** '__ALL__' = 전체, 그 외 = 해당 심볼만 */
  const [symbolFilter, setSymbolFilter]   = useState("__ALL__");

  const fetchImplRef = useRef(fetchImpl ?? defaultFetchImpl);
  fetchImplRef.current = fetchImpl ?? defaultFetchImpl;

  const sourceRows = activeTab === "second"
    ? (viewMode === "pending" ? secondPending : secondHistory)
    : (viewMode === "pending" ? mainPending : mainHistory);
  const activeHasMore = activeTab === "second" ? secondHasMore : mainHasMore;
  const activeNextBeforeId = activeTab === "second" ? secondNextBeforeId : mainNextBeforeId;

  const uniqueSymbols = useMemo(() => {
    const s = new Set();
    sourceRows.forEach((r) => {
      if (r?.symbol) s.add(r.symbol);
    });
    return Array.from(s).sort((a, b) => a.localeCompare(b, "en"));
  }, [sourceRows]);

  const uniqueKey = uniqueSymbols.join("\n");

  useEffect(() => {
    setSymbolFilter("__ALL__");
  }, [activeTab, uniqueKey, viewMode]);

  const filteredRows = useMemo(() => {
    if (!symbolFilter || symbolFilter === "__ALL__") return sourceRows;
    return sourceRows.filter((r) => r.symbol === symbolFilter);
  }, [sourceRows, symbolFilter]);

  const loadPendingOrders = useCallback(async () => {
    if (!pendingOrdersEndpoint) return;
    const response = await fetchImplRef.current(pendingOrdersEndpoint, {});
    const data = await response.json().catch(() => ({}));
    if (!response.ok || (data?.message && !data?.main_pending)) {
      throw new Error(data?.message || "대기주문 조회에 실패했습니다.");
    }
    setMainPending(Array.isArray(data?.main_pending) ? data.main_pending : []);
    setSecondPending(Array.isArray(data?.second_pending) ? data.second_pending : []);
    setHasSecondAccount(Boolean(data?.has_second_account));
  }, [pendingOrdersEndpoint]);

  const handleCancelPendingOrder = useCallback(async (item) => {
    if (!cancelPendingOrderEndpoint || item?.id == null || item?.id === "") return;
    const orderId = String(item.id);
    const kindLabel = pendingKindLabel(item.order_kind);
    const confirmed = window.confirm(
      `[${item.symbol}] ${kindLabel} 대기 주문을 취소할까요?\n\n주문 ID: ${orderId}`
    );
    if (!confirmed) return;
    setCancellingId(orderId);
    setCancelNotice("");
    try {
      const response = await fetchImplRef.current(cancelPendingOrderEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          order_id: orderId,
          symbol: item.symbol,
          order_kind: item.order_kind,
          plan_type: item.plan_type || undefined,
          account: activeTab === "second" ? "second" : "main",
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data?.message) {
        throw new Error(data?.message || "대기주문 취소에 실패했습니다.");
      }
      await loadPendingOrders();
      setCancelNotice("선택한 대기 주문을 취소했습니다.");
    } catch (err) {
      setCancelNotice(err?.message || "대기주문 취소에 실패했습니다.");
    } finally {
      setCancellingId(null);
    }
  }, [activeTab, cancelPendingOrderEndpoint, loadPendingOrders]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    setLoadMoreError("");

    const endpoint = viewMode === "pending" ? pendingOrdersEndpoint : historyEndpoint;
    if (!endpoint) {
      setLoading(false);
      setError(viewMode === "pending" ? "대기주문 조회를 지원하지 않습니다." : "주문내역 조회를 지원하지 않습니다.");
      return undefined;
    }

    if (viewMode === "pending") {
      fetchImplRef.current(endpoint, {})
        .then((r) => r.json().catch(() => ({})))
        .then((data) => {
          if (cancelled) return;
          if (data?.message && !data?.main_pending) { setError(data.message); return; }
          setMainPending(Array.isArray(data?.main_pending) ? data.main_pending : []);
          setSecondPending(Array.isArray(data?.second_pending) ? data.second_pending : []);
          setHasSecondAccount(Boolean(data?.has_second_account));
          setCancelNotice("");
        })
        .catch((err) => {
          if (!cancelled) setError(err?.message || "대기주문 조회에 실패했습니다.");
        })
        .finally(() => { if (!cancelled) setLoading(false); });
      return () => { cancelled = true; };
    }

    setMainHistory([]);
    setSecondHistory([]);
    setMainHasMore(false);
    setSecondHasMore(false);
    setMainNextBeforeId(null);
    setSecondNextBeforeId(null);

    fetchImplRef.current(historyEndpoint, {})
      .then((r) => r.json().catch(() => ({})))
      .then((data) => {
        if (cancelled) return;
        if (data?.message && !data?.main_history) { setError(data.message); return; }
        setMainHistory(Array.isArray(data?.main_history) ? data.main_history : []);
        setSecondHistory(Array.isArray(data?.second_history) ? data.second_history : []);
        setHasSecondAccount(Boolean(data?.has_second_account));
        setMainHasMore(Boolean(data?.main_has_more));
        setSecondHasMore(Boolean(data?.second_has_more));
        setMainNextBeforeId(data?.main_next_before_id || null);
        setSecondNextBeforeId(data?.second_next_before_id || null);
      })
      .catch((err) => { if (!cancelled) setError(err?.message || "주문내역 조회에 실패했습니다."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [historyEndpoint, pendingOrdersEndpoint, viewMode]);

  const handleLoadMore = useCallback(async () => {
    if (viewMode !== "filled" || !activeHasMore || !activeNextBeforeId || loadingMore) return;
    const account = activeTab === "second" ? "second" : "main";
    setLoadingMore(true);
    setLoadMoreError("");
    try {
      const url = buildHistoryUrl(historyEndpoint, {
        account,
        beforeId: activeNextBeforeId,
      });
      const response = await fetchImplRef.current(url, {});
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data?.message) {
        throw new Error(data?.message || "추가 내역 조회에 실패했습니다.");
      }
      const nextRows = Array.isArray(data?.history) ? data.history : [];
      if (account === "second") {
        setSecondHistory((prev) => appendUniqueHistoryRows(prev, nextRows));
        setSecondHasMore(Boolean(data?.has_more));
        setSecondNextBeforeId(data?.next_before_id || null);
      } else {
        setMainHistory((prev) => appendUniqueHistoryRows(prev, nextRows));
        setMainHasMore(Boolean(data?.has_more));
        setMainNextBeforeId(data?.next_before_id || null);
      }
    } catch (err) {
      setLoadMoreError(err?.message || "추가 내역 조회에 실패했습니다.");
    } finally {
      setLoadingMore(false);
    }
  }, [activeHasMore, activeNextBeforeId, activeTab, historyEndpoint, loadingMore, viewMode]);

  useEffect(() => {
    const handleKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [onClose]);

  return createPortal(
    (
    <div
      className="order-history-modal__overlay"
      role="presentation"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="order-history-modal__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="order-history-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="order-history-modal__header">
          <span id="order-history-modal-title" className="order-history-modal__title">
            {viewMode === "pending" ? "대기 주문" : "주문 내역"}
          </span>
          <button type="button" className="order-history-modal__close" onClick={onClose} aria-label="닫기">
            ✕
          </button>
        </div>

        <div className="order-history-modal__tabs order-history-modal__tabs--view">
          <button
            type="button"
            className={`order-history-modal__tab ${viewMode === "filled" ? "is-active" : ""}`}
            onClick={() => setViewMode("filled")}
          >
            체결 내역
          </button>
          {pendingOrdersEndpoint ? (
            <button
              type="button"
              className={`order-history-modal__tab ${viewMode === "pending" ? "is-active" : ""}`}
              onClick={() => setViewMode("pending")}
            >
              대기 주문
            </button>
          ) : null}
        </div>

        {hasSecondAccount && (
          <div className="order-history-modal__tabs">
            <button type="button" className={`order-history-modal__tab ${activeTab === "main" ? "is-active" : ""}`} onClick={() => setActiveTab("main")}>메인계정</button>
            <button type="button" className={`order-history-modal__tab ${activeTab === "second" ? "is-active" : ""}`} onClick={() => setActiveTab("second")}>보조계정</button>
          </div>
        )}

        <div className="order-history-modal__body">
          {loading ? (
            <div className="order-history-modal__status">조회 중...</div>
          ) : error ? (
            <div className="order-history-modal__status is-error">{error}</div>
          ) : sourceRows.length === 0 ? (
            <div className="order-history-modal__status">
              {viewMode === "pending" ? "대기 중인 주문이 없습니다." : "주문 내역이 없습니다."}
            </div>
          ) : (
            <>
              {uniqueSymbols.length > 0 ? (
                <div className="order-history-modal__symbol-filter">
                  <label htmlFor="order-history-symbol-select" className="order-history-modal__symbol-filter-label">
                    심볼
                  </label>
                  <select
                    id="order-history-symbol-select"
                    className="order-history-modal__symbol-select"
                    value={symbolFilter}
                    onChange={(e) => setSymbolFilter(e.target.value)}
                  >
                    <option value="__ALL__">전체</option>
                    {uniqueSymbols.map((sym) => (
                      <option key={sym} value={sym}>
                        {sym}
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}
              {viewMode === "pending" && cancelNotice ? (
                <div
                  className={`order-history-modal__status ${cancelNotice.includes("실패") ? "is-error" : ""}`}
                  style={{ paddingTop: 8, paddingBottom: 0 }}
                >
                  {cancelNotice}
                </div>
              ) : null}
              {filteredRows.length === 0 ? (
                <div className="order-history-modal__status">표시할 내역이 없습니다.</div>
              ) : (
            <div className="order-history-modal__table-wrap">
              {viewMode === "pending" ? (
              <table className="order-history-modal__table">
                <thead>
                  <tr>
                    <th scope="col">등록</th>
                    <th scope="col">종류</th>
                    <th scope="col">구분</th>
                    <th scope="col">심볼</th>
                    <th scope="col">타입</th>
                    <th scope="col">트리거 / 지정가</th>
                    <th scope="col">수량</th>
                    <th scope="col">상태</th>
                    <th scope="col">주문ID</th>
                    {cancelPendingOrderEndpoint ? <th scope="col">취소</th> : null}
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((item, index) => (
                    <tr key={item.id ?? index} className={index % 2 === 1 ? "is-even" : ""}>
                      <td className="order-history-modal__cell order-history-modal__cell--time">
                        {item.timestamp
                          ? new Date(item.timestamp).toLocaleString("ko-KR", {
                              month: "2-digit", day: "2-digit",
                              hour: "2-digit", minute: "2-digit", second: "2-digit",
                              hour12: false,
                            })
                          : "-"}
                      </td>
                      <td className={`order-history-modal__cell order-history-modal__kind ${pendingKindClass(item.order_kind)}`}>
                        {pendingKindLabel(item.order_kind)}
                      </td>
                      <td className={`order-history-modal__cell order-history-modal__action ${actionClass(item.action)}`}>
                        {item.action || "-"}
                      </td>
                      <td className="order-history-modal__cell">{item.symbol ?? "-"}</td>
                      <td className="order-history-modal__cell">{item.type ?? "-"}</td>
                      <td className="order-history-modal__cell order-history-modal__cell--stacked">
                        {item.order_kind === "trigger" ? (
                          <>
                            <div className="order-history-modal__stack-line">
                              <span className="order-history-modal__stack-label">트리거</span>
                              <span>{formatNullableNumber(item.trigger_price, 4)}</span>
                            </div>
                            {item.price != null ? (
                              <div className="order-history-modal__stack-line">
                                <span className="order-history-modal__stack-label">체결</span>
                                <span>{formatNullableNumber(item.price, 4)}</span>
                              </div>
                            ) : null}
                          </>
                        ) : (
                          formatNullableNumber(item.price, 4)
                        )}
                      </td>
                      <td className="order-history-modal__cell order-history-modal__cell--stacked">
                        <div className="order-history-modal__stack-line">
                          <span className="order-history-modal__stack-label">주문</span>
                          <span>{formatSnapshotNumber(item.amount, 4)}</span>
                        </div>
                        {item.remaining != null ? (
                          <div className="order-history-modal__stack-line">
                            <span className="order-history-modal__stack-label">잔량</span>
                            <span>{formatSnapshotNumber(item.remaining, 4)}</span>
                          </div>
                        ) : null}
                      </td>
                      <td className="order-history-modal__cell">{item.status ?? "-"}</td>
                      <td className="order-history-modal__cell order-history-modal__cell--id">{item.id ?? "-"}</td>
                      {cancelPendingOrderEndpoint ? (
                        <td className="order-history-modal__cell order-history-modal__cell--action">
                          <button
                            type="button"
                            className="order-history-modal__cancel-btn"
                            disabled={cancellingId != null}
                            onClick={() => handleCancelPendingOrder(item)}
                          >
                            {cancellingId === String(item.id ?? "") ? "취소 중…" : "취소"}
                          </button>
                        </td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
              ) : (
              <table className="order-history-modal__table">
                <thead>
                  <tr>
                    <th scope="col">시간</th>
                    <th scope="col">구분</th>
                    <th scope="col">심볼</th>
                    <th scope="col">타입</th>
                    <th scope="col">평균진입 / 청산</th>
                    <th scope="col">실현 PnL</th>
                    <th scope="col">ROI</th>
                    <th scope="col">체결가</th>
                    <th scope="col">수량</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((item, index) => (
                    <tr key={item.id ?? index} className={index % 2 === 1 ? "is-even" : ""}>
                      <td className="order-history-modal__cell order-history-modal__cell--time">
                        {item.timestamp
                          ? new Date(item.timestamp).toLocaleString("ko-KR", {
                              month: "2-digit", day: "2-digit",
                              hour: "2-digit", minute: "2-digit", second: "2-digit",
                              hour12: false,
                            })
                          : "-"}
                      </td>
                      <td className={`order-history-modal__cell order-history-modal__action ${actionClass(item.action)}`}>
                        {item.action || "-"}
                      </td>
                      <td className="order-history-modal__cell">{item.symbol ?? "-"}</td>
                      <td className="order-history-modal__cell">{item.type ?? "-"}</td>
                      <td className="order-history-modal__cell order-history-modal__cell--stacked">
                        {isCloseHistoryAction(item.action) ? (
                          <>
                            <div className="order-history-modal__stack-line">
                              <span className="order-history-modal__stack-label">진입</span>
                              <span>{formatNullableNumber(item.avg_entry_price, 4)}</span>
                            </div>
                            <div className="order-history-modal__stack-line">
                              <span className="order-history-modal__stack-label">청산</span>
                              <span>{formatNullableNumber(item.avg_exit_price, 4)}</span>
                            </div>
                          </>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className={`order-history-modal__cell ${pnlToneClass(item.realized_pnl)}`}>
                        {isCloseHistoryAction(item.action) && item.realized_pnl != null && Number.isFinite(Number(item.realized_pnl))
                          ? `${Number(item.realized_pnl) > 0 ? "+" : ""}${formatSnapshotNumber(item.realized_pnl, 4)} USDT`
                          : "—"}
                      </td>
                      <td className={`order-history-modal__cell ${pnlToneClass(item.roi_percent)}`}>
                        {isCloseHistoryAction(item.action) && item.roi_percent != null && Number.isFinite(Number(item.roi_percent))
                          ? `${Number(item.roi_percent) > 0 ? "+" : ""}${formatSnapshotNumber(item.roi_percent, 2)}%`
                          : "—"}
                      </td>
                      <td className="order-history-modal__cell">{formatSnapshotNumber(item.price, 4)}</td>
                      <td className="order-history-modal__cell">{formatSnapshotNumber(item.filled, 4)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              )}
            </div>
              )}
              {viewMode === "filled" && activeHasMore && activeNextBeforeId ? (
                <div className="order-history-modal__footer">
                  {loadMoreError ? (
                    <div className="order-history-modal__status is-error">{loadMoreError}</div>
                  ) : null}
                  <button
                    type="button"
                    className="order-history-modal__load-more"
                    onClick={handleLoadMore}
                    disabled={loadingMore}
                  >
                    {loadingMore ? "불러오는 중..." : "더 보기 (100건)"}
                  </button>
                </div>
              ) : null}
            </>
          )}
        </div>
      </div>
    </div>
    ),
    document.body
  );
}

// ─── 계좌 잔고 ──────────────────────────────────────────────────────────────
export function OpenPositionsSnapshotAccountBalances({ wallet, available }) {
  return (
    <div className="hold-position-snapshot__account-balances">
      <div className="hold-position-snapshot__balance-row">
        <span>Wallet</span>
        <strong>{formatSnapshotNumber(wallet, 2)} USDT</strong>
      </div>
      <div className="hold-position-snapshot__balance-row">
        <span>Available</span>
        <strong>{formatSnapshotNumber(available, 2)} USDT</strong>
      </div>
    </div>
  );
}

// ─── 메인 컴포넌트 ──────────────────────────────────────────────────────────
export default function OpenPositionsSnapshotSection({
  endpoint,
  enabled = true,
  reloadKey,
  onSelectSymbol,
  selectedSymbol,
  fetchImpl,
  title = "현재 오픈 포지션",
  updatedAtPrefix = "최근 갱신:",
  loadingRefreshLabel = "갱신 중...",
  showAdminManualTrade = false,
  onAdminNewEntry,
  onAdminAdjustLeg,
}) {
  const liveEndpoint    = endpoint ? endpoint.replace(/_snapshot$/, "_live") : null;
  const historyEndpoint = endpoint
    ? endpoint.replace(/_open_positions_snapshot$/, "_order_history")
    : null;
  const pendingOrdersEndpoint = endpoint
    ? endpoint.replace(/_open_positions_snapshot$/, "_pending_orders")
    : null;
  const cancelPendingOrderEndpoint = endpoint
    ? endpoint.replace(/_open_positions_snapshot$/, "_cancel_pending_order")
    : null;

  const [loading, setLoading]                                 = useState(false);
  const [liveLoading, setLiveLoading]                         = useState(false);
  const [error, setError]                                     = useState("");
  const [updatedAt, setUpdatedAt]                             = useState(null);
  const [isLive, setIsLive]                                   = useState(false);
  const [mainOpenPositionItems, setMainOpenPositionItems]     = useState([]);
  const [secondOpenPositionItems, setSecondOpenPositionItems] = useState([]);
  const [hasSecondAccount, setHasSecondAccount]               = useState(false);
  const [mainWalletBalance, setMainWalletBalance]             = useState(null);
  const [mainAvailable, setMainAvailable]                     = useState(null);
  const [secondWalletBalance, setSecondWalletBalance]         = useState(null);
  const [secondAvailable, setSecondAvailable]                 = useState(null);
  const [showHistory, setShowHistory]                         = useState(false);

  const fetchImplRef   = useRef(fetchImpl ?? defaultFetchImpl);
  fetchImplRef.current = fetchImpl ?? defaultFetchImpl;

  const applyPositionData = (data) => {
    setHasSecondAccount(Boolean(data?.has_second_account));
    setMainOpenPositionItems(Array.isArray(data?.main_positions) ? data.main_positions : []);
    setSecondOpenPositionItems(Array.isArray(data?.second_positions) ? data.second_positions : []);
    setUpdatedAt(data?.updated_at ?? null);
    setMainWalletBalance(data?.main_wallet_balance ?? null);
    setMainAvailable(data?.main_available ?? null);
    setSecondWalletBalance(data?.second_wallet_balance ?? null);
    setSecondAvailable(data?.second_available ?? null);
    setIsLive(Boolean(data?.is_live));
  };

  const fetchSnapshot = useCallback(
    async ({ silent = false } = {}) => {
      if (!enabled) return;
      setLoading(true);
      setError("");
      try {
        const response = await fetchImplRef.current(endpoint, {});
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data?.message || "오픈 포지션 스냅샷 조회에 실패했습니다.");
        applyPositionData(data);
      } catch (fetchError) {
        const message = fetchError?.message || "오픈 포지션 스냅샷 조회에 실패했습니다.";
        console.error(`Open positions snapshot error (${endpoint}):`, fetchError);
        setError(message);
        if (!silent) alert(message);
      } finally {
        setLoading(false);
      }
    },
    [enabled, endpoint]
  );

  const fetchLive = useCallback(
    async ({ silent = false } = {}) => {
      if (!liveEndpoint || !enabled) return;
      setLiveLoading(true);
      setError("");
      try {
        const response = await fetchImplRef.current(liveEndpoint, {});
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data?.message || "포지션 조회에 실패했습니다.");
        applyPositionData(data);
      } catch (fetchError) {
        const message = fetchError?.message || "포지션 조회에 실패했습니다.";
        console.error(`Open positions live error (${liveEndpoint}):`, fetchError);
        setError(message);
        if (!silent) alert(message);
      } finally {
        setLiveLoading(false);
      }
    },
    [liveEndpoint, enabled]
  );

  useEffect(() => {
    if (!enabled) return;
    fetchSnapshot({ silent: true });
  }, [enabled, reloadKey, fetchSnapshot]);

  if (!enabled) return null;

  const handleSymbolClick = (symbol) => {
    if (!symbol || typeof onSelectSymbol !== "function") return;
    onSelectSymbol(symbol);
  };

  const handleSymbolKeyDown = (event, symbol) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleSymbolClick(symbol);
    }
  };

  const adminTable =
    showAdminManualTrade && typeof onAdminAdjustLeg === "function"
      ? " hold-position-snapshot__table--with-admin"
      : "";

  const isInitLoading = loading && !liveLoading;

  const handleHistoryOpen = () => {
    setShowHistory(true);
  };

  return (
    <>
      <section className="hold-position-snapshot">
        <div className="hold-position-snapshot__header">
          <div>
            <div className="center_text_lever" style={{ marginBottom: "8px" }}>{title}</div>
            <div className="hold-position-snapshot__meta">
              {updatedAtPrefix} {formatSnapshotTime(updatedAt)}
              {isLive && <span className="hold-position-snapshot__live-badge"> ● LIVE</span>}
              {isInitLoading && <span style={{ marginLeft: "6px", opacity: 0.6 }}>로딩 중...</span>}
            </div>
          </div>
          <div className="hold-position-snapshot__header-actions">
            {showAdminManualTrade && typeof onAdminNewEntry === "function" ? (
              <button
                type="button"
                className="button_benefit_c_lever hold-position-snapshot__action-btn"
                onClick={(e) => onAdminNewEntry(e)}
              >
                USDT 신규
              </button>
            ) : null}
            {historyEndpoint ? (
              <button
                type="button"
                className="button_benefit_c_lever hold-position-snapshot__action-btn"
                onClick={handleHistoryOpen}
                disabled={liveLoading}
              >
                HISTORY
              </button>
            ) : null}
            {liveEndpoint ? (
              <button
                type="button"
                className="button_benefit_c_lever hold-position-snapshot__action-btn"
                onClick={() => fetchLive({ silent: false })}
                disabled={liveLoading}
              >
                {liveLoading ? loadingRefreshLabel : "새로고침"}
              </button>
            ) : null}
          </div>
        </div>

        {error ? <div className="hold-position-snapshot__status is-error">{error}</div> : null}

        <div className="hold-position-snapshot__grid">
          {/* 메인계정 */}
          <div className="hold-position-snapshot__account">
            <div className="hold-position-snapshot__account-head">
              <div className="hold-position-snapshot__account-title">
                메인계정
                <span className="hold-position-snapshot__count">{mainOpenPositionItems.length}건</span>
              </div>
              <OpenPositionsSnapshotAccountBalances wallet={mainWalletBalance} available={mainAvailable} />
            </div>
            {mainOpenPositionItems.length === 0 ? (
              <div className="hold-position-snapshot__empty">오픈 포지션 없음</div>
            ) : (
              <div className={`hold-position-snapshot__table${adminTable}`}>
                <div className="hold-position-snapshot__table-head">
                  <span>심볼</span><span>방향</span><span>진입마진</span>
                  <span>평단</span><span>손익비</span><span>미실현</span>
                  {showAdminManualTrade && onAdminAdjustLeg ? <span></span> : null}
                </div>
                {mainOpenPositionItems.map((item, index) => (
                  <div key={`${item.symbol}-${item.side}`} className={`hold-position-snapshot__row ${index % 2 === 1 ? "is-even" : ""}`}>
                    <span className="hold-position-snapshot__cell hold-position-snapshot__cell--symbol">
                      {typeof onSelectSymbol === "function" ? (
                        <button
                          type="button"
                          className={`hold-position-snapshot__symbol-button ${selectedSymbol === item.symbol ? "is-selected" : ""}`}
                          onClick={() => handleSymbolClick(item.symbol)}
                          onKeyDown={(event) => handleSymbolKeyDown(event, item.symbol)}
                        >{item.symbol}</button>
                      ) : item.symbol}
                    </span>
                    <span className="hold-position-snapshot__cell" data-label="방향">
                      <span className={`hold-position-snapshot__side hold-position-snapshot__side--${item.side}`}>
                        {item.side === "long" ? "롱" : "숏"}
                      </span>
                    </span>
                    <span className="hold-position-snapshot__cell" data-label="진입마진">{formatSnapshotNumber(item.margin, 3)}</span>
                    <span className="hold-position-snapshot__cell" data-label="평단">{formatSnapshotNumber(item.entryPrice, 4)}</span>
                    <span className="hold-position-snapshot__cell" data-label="손익비">{formatSnapshotNumber(item.roiPercent, 2)}%</span>
                    <span className="hold-position-snapshot__cell" data-label="미실현">{formatSnapshotNumber(item.unrealizedPnl, 3)}</span>
                    {showAdminManualTrade && onAdminAdjustLeg ? (
                      <span className="hold-position-snapshot__cell hold-position-snapshot__cell--admin">
                        <button type="button" className="hold-position-snapshot__admin-leg"
                          onClick={(e) => onAdminAdjustLeg({ account: "main", item: snapshotItemForAdminLeg(item), anchorRect: e.currentTarget.getBoundingClientRect() })}>
                          조정
                        </button>
                      </span>
                    ) : null}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* 보조계정 */}
          {hasSecondAccount ? (
            <div className="hold-position-snapshot__account">
              <div className="hold-position-snapshot__account-head">
                <div className="hold-position-snapshot__account-title">
                  보조계정
                  <span className="hold-position-snapshot__count">{secondOpenPositionItems.length}건</span>
                </div>
                <OpenPositionsSnapshotAccountBalances wallet={secondWalletBalance} available={secondAvailable} />
              </div>
              {secondOpenPositionItems.length === 0 ? (
                <div className="hold-position-snapshot__empty">오픈 포지션 없음</div>
              ) : (
                <div className={`hold-position-snapshot__table${adminTable}`}>
                  <div className="hold-position-snapshot__table-head">
                    <span>심볼</span><span>방향</span><span>진입마진</span>
                    <span>평단</span><span>손익비</span><span>미실현</span>
                    {showAdminManualTrade && onAdminAdjustLeg ? <span></span> : null}
                  </div>
                  {secondOpenPositionItems.map((item, index) => (
                    <div key={`${item.symbol}-${item.side}`} className={`hold-position-snapshot__row ${index % 2 === 1 ? "is-even" : ""}`}>
                      <span className="hold-position-snapshot__cell hold-position-snapshot__cell--symbol">
                        {typeof onSelectSymbol === "function" ? (
                          <button
                            type="button"
                            className={`hold-position-snapshot__symbol-button ${selectedSymbol === item.symbol ? "is-selected" : ""}`}
                            onClick={() => handleSymbolClick(item.symbol)}
                            onKeyDown={(event) => handleSymbolKeyDown(event, item.symbol)}
                          >{item.symbol}</button>
                        ) : item.symbol}
                      </span>
                      <span className="hold-position-snapshot__cell" data-label="방향">
                        <span className={`hold-position-snapshot__side hold-position-snapshot__side--${item.side}`}>
                          {item.side === "long" ? "롱" : "숏"}
                        </span>
                      </span>
                      <span className="hold-position-snapshot__cell" data-label="진입마진">{formatSnapshotNumber(item.margin, 3)}</span>
                      <span className="hold-position-snapshot__cell" data-label="평단">{formatSnapshotNumber(item.entryPrice, 4)}</span>
                      <span className="hold-position-snapshot__cell" data-label="손익비">{formatSnapshotNumber(item.roiPercent, 2)}%</span>
                      <span className="hold-position-snapshot__cell" data-label="미실현">{formatSnapshotNumber(item.unrealizedPnl, 3)}</span>
                      {showAdminManualTrade && onAdminAdjustLeg ? (
                        <span className="hold-position-snapshot__cell hold-position-snapshot__cell--admin">
                          <button type="button" className="hold-position-snapshot__admin-leg"
                            onClick={(e) => onAdminAdjustLeg({ account: "second", item: snapshotItemForAdminLeg(item), anchorRect: e.currentTarget.getBoundingClientRect() })}>
                            조정
                          </button>
                        </span>
                      ) : null}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : null}
        </div>
      </section>

      {showHistory && historyEndpoint ? (
        <OrderHistoryModal
          historyEndpoint={historyEndpoint}
          pendingOrdersEndpoint={pendingOrdersEndpoint}
          cancelPendingOrderEndpoint={cancelPendingOrderEndpoint}
          fetchImpl={fetchImpl}
          onClose={() => setShowHistory(false)}
        />
      ) : null}
    </>
  );
}
